import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";

import { Button, PageHeader } from "@cofano/cck";
import { CircularProgress } from "@mui/material";

import Helmet from "components/helmet/Helmet";

import { hasPluginPermission, Language, usePermission } from "utils";

import { VoyageInfo } from "view/voyages/components/details/VoyageInfo";

import { GetActiveVoyageDocument, GetVoyageDocument } from "__generated__/view/voyages/graphql/voyage.queries.generated";

const SkipperVoyageDetails = (props: { pathId?: number }) => {
	const [ searchParams ] = useSearchParams();

	const voyageId = props.pathId;
	const shipId = searchParams.get("ship") ? Number(searchParams.get("ship")) : undefined;

	const { voyage, refetch, loading } = useSkipperVoyageQuery(voyageId, shipId);

	const { isReadOnly } = usePermission();
	const navigate = useNavigate();
	const session = useStoreState((state) => state.session);

	return <>
		<PageHeader>
			<PageHeader.Title>
				{Language("VOYAGE")}
				<Helmet title={intl.formatMessage({ id: "VOYAGE" })} />
			</PageHeader.Title>

			<PageHeader.Actions>
				{!loading && voyage && (voyage.calls.length > 0) && !isReadOnly && (
					<React.Fragment>
						{voyage!.sailingReport ? (
							<Button
								onClick={() => navigate(`/view/sailingreports/${voyage.sailingReport?.id}/edit`)}
								variant="outlined">
								{Language("SAILING_REPORT_EDIT")}
							</Button>
						) : (
							<Button
								onClick={() => navigate("/view/sailingreports/create", { state: { voyage: voyage } })}
								variant="outlined">
								{Language("SAILING_REPORT_ADD")}
							</Button>
						)}

						{hasPluginPermission(session, "angler") && <Button
							onClick={() => navigate(`/view/voyages/${voyage?.id}/plan`)}
							variant="outlined">
							{Language("ANGLER_VOYAGE_PLAN")}
						</Button>}
					</React.Fragment>
				)}
			</PageHeader.Actions>
		</PageHeader>

		{loading && <CircularProgress size={24} />}

		{voyage ? (
			<VoyageInfo voyage={voyage} refetch={refetch} withoutTabs />
		) : (
			!loading && intl.formatMessage({ id: "NO_VOYAGES_FOUND" })
		)}
	</>;
};

const useSkipperVoyageQuery = (voyageId: number | undefined, shipId: number | undefined) => {
	const queriedVoyage = useQuery(GetVoyageDocument, {
		variables: { id: voyageId! },
		skip: !voyageId,
	});

	const activeVoyage = useQuery(GetActiveVoyageDocument, {
		variables: { shipId: shipId },
		skip: !!voyageId,
	});

	const voyage = voyageId ? queriedVoyage.data?.voyage : activeVoyage.data?.activeVoyage;
	const refetch = voyageId ? queriedVoyage.refetch : activeVoyage.refetch;
	const loading = voyageId ? queriedVoyage.loading : activeVoyage.loading;

	return { voyage, refetch, loading };
};

export default SkipperVoyageDetails;
